import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import {
  Box,
  Collapse,
  Grid,
  IconButton,
  Skeleton,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckboxInput,
  PrimaryButton,
  SectionLabel,
  TableAction,
  TablePagination,
} from "components";
import { Fragment, useState } from "react";
import { useSelector } from "react-redux";

const CustomTable = ({
  columnTable,
  data = [],
  rowClick,
  emptyPlaceHolder,
  page,
  pageSize,
  pageCount,
  handleChangePage,
  handleChangePageSize,
  isLoading,
  color,
  maxHeight,
  tableSize,
  isHaveAction,
  renderAction,
  handleView,
  deleteConfirmation,
  deleteConfirmationContent,
  deleteConfirmationKey,
  deleteConfirmationTitle,
  handleRestore,
  restoreConfirmation,
  restoreConfirmationContent,
  restoreConfirmationKey,
  restoreConfirmationTitle,
  tooltipDelete,
  tooltipEdit,
  tooltipRestore,
  tooltipView,
  handleEdit,
  handleDelete,
  disabledView,
  disabledEdit,
  disabledDelete,
  disabledRestore,
  skeletonHeight = 300,
  pageSizeList,
  onRequestSort,
  sortValue,
  checkboxHeader,
  checked,
  handleCheckbox,
  checkedAction,
  isHaveCheckbox,
  handleCheckboxAction,
  customAction,
  tooltipCheckbox,
  disabledCheckbox,
  hiddenCheckbox,
  tooltipCheckboxAction,
  disabledCheckboxAction,
  checkboxHeaderChecked,
  disabledCheckboxHeaderChecked,
  handleCheckboxHeader,
  hideLastPage,
  isExpandable,
  renderExpandedRow,
  expandedRowKey,
  handleExpandableRow,
  sectionTitle,
  sectionSubTitle,
  sectionButtonLabel,
  handleSectionButton,
  ...rest
}) => {
  const userState = useSelector(state => state?.userState);
  const theme = userState?.publisher?.theme_color;
  const [sortBy, sortDir] = sortValue?.split(" ") || [];
  const [openRow, setOpenRow] = useState(null);

  const handleToggleRow = row => {
    const key = row?.[expandedRowKey] || row;
    setOpenRow(prev => (prev === key ? null : key));
  };

  return (
    <Box border="1px solid #ebebeb" borderRadius="6px" {...rest}>
      {isLoading ? (
        <Skeleton
          variant="rounded"
          height={skeletonHeight || maxHeight + 74}
          animation="wave"
        />
      ) : (
        <Box>
          {!!sectionTitle && (
            <Box borderBottom="1px solid #ebebeb" bgcolor="#FAFAFA" p="16px">
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
              >
                <Grid item>
                  <SectionLabel
                    title={sectionTitle}
                    subTitle={sectionSubTitle}
                  />
                </Grid>
                {!!sectionButtonLabel && (
                  <Grid item>
                    <PrimaryButton
                      label={sectionButtonLabel}
                      onClick={handleSectionButton}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
          <CustomTableContainer maxHeight={maxHeight}>
            <Table stickyHeader size={tableSize}>
              <TableHead>
                <TableRow>
                  {isHaveCheckbox && (
                    <TableHeadCell width={50}>
                      {typeof checkboxHeader === "string" ? (
                        <Typography
                          fontSize="14px"
                          color="#687083"
                          fontWeight="bold"
                        >
                          {checkboxHeader}
                        </Typography>
                      ) : handleCheckboxHeader ? (
                        <CheckboxInput
                          checked={checkboxHeaderChecked}
                          disabled={disabledCheckboxHeaderChecked}
                          color={disabledCheckboxHeaderChecked && "gray"}
                          onChange={
                            disabledCheckboxHeaderChecked
                              ? null
                              : e => handleCheckboxHeader(e)
                          }
                        />
                      ) : (
                        checkboxHeader
                      )}
                    </TableHeadCell>
                  )}
                  {isHaveAction && (
                    <TableHeadCell width={150}>
                      <Typography
                        fontSize="14px"
                        color="#687083"
                        fontWeight="bold"
                      >
                        Action
                      </Typography>
                    </TableHeadCell>
                  )}
                  {columnTable?.map((item, index) => (
                    <TableHeadCell
                      key={index}
                      width={item?.width}
                      align={item?.headerAlign}
                      dense={item?.dense}
                    >
                      {item?.useSort ? (
                        <TableSortLabel
                          active={sortBy === item?.sort_key}
                          direction={
                            sortBy === item?.sort_key ? sortDir : "asc"
                          }
                          onClick={() => onRequestSort?.(item?.sort_key)}
                        >
                          <Fragment>
                            <Typography
                              component="span"
                              fontSize="14px"
                              color="#687083"
                              fontWeight="bold"
                            >
                              {item?.title}
                            </Typography>
                            {!!item?.required && (
                              <Typography component="span" color="red" ml="5px">
                                *
                              </Typography>
                            )}
                          </Fragment>
                        </TableSortLabel>
                      ) : (
                        <Fragment>
                          <Typography
                            component="span"
                            fontSize="14px"
                            color="#687083"
                            fontWeight="bold"
                          >
                            {item?.title}
                          </Typography>
                          {!!item?.required && (
                            <Typography component="span" color="red" ml="5px">
                              *
                            </Typography>
                          )}
                        </Fragment>
                      )}
                    </TableHeadCell>
                  ))}
                  {isExpandable && <TableHeadCell width={50} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.length > 0 ? (
                  data?.map((value, index) => (
                    <Fragment key={index}>
                      <TableRow
                        hover
                        style={
                          rowClick && {
                            cursor: "pointer",
                          }
                        }
                        onClick={event => {
                          if (rowClick) {
                            event.preventDefault();
                            rowClick(value);
                          }
                        }}
                      >
                        {isHaveCheckbox && (
                          <Tooltip
                            title={
                              typeof tooltipCheckbox === "function"
                                ? tooltipCheckbox(value, index)
                                : tooltipCheckbox
                            }
                            placement="bottom"
                          >
                            <TableBodyCell
                              maxWidth={`${1 / columnTable?.length}%`}
                            >
                              {!(typeof hiddenCheckbox === "function"
                                ? hiddenCheckbox(value, index)
                                : hiddenCheckbox) && (
                                <CheckboxInput
                                  checked={
                                    typeof checked === "function"
                                      ? checked(value, index)
                                      : checked
                                  }
                                  disabled={
                                    typeof disabledCheckbox === "function"
                                      ? disabledCheckbox(value, index)
                                      : disabledCheckbox
                                  }
                                  color={
                                    (typeof disabledCheckbox === "function"
                                      ? disabledCheckbox(value, index)
                                      : disabledCheckbox) && "gray"
                                  }
                                  onChange={
                                    (typeof disabledCheckbox === "function"
                                    ? disabledCheckbox(value, index)
                                    : disabledCheckbox)
                                      ? null
                                      : e => handleCheckbox(e, value, index)
                                  }
                                />
                              )}
                            </TableBodyCell>
                          </Tooltip>
                        )}
                        {isHaveAction && (
                          <TableBodyCell
                            maxWidth={`${1 / columnTable?.length}%`}
                          >
                            {renderAction ? (
                              renderAction(value)
                            ) : (
                              <TableAction
                                {...{
                                  handleView:
                                    handleView &&
                                    (() => handleView(value, index)),
                                  handleRestore:
                                    handleRestore &&
                                    (() => handleRestore(value, index)),
                                  handleEdit:
                                    handleEdit &&
                                    (() => handleEdit(value, index)),
                                  handleDelete:
                                    handleDelete &&
                                    (() => handleDelete(value, index)),
                                  deleteConfirmation,
                                  restoreConfirmation,
                                  deleteConfirmationContent:
                                    typeof deleteConfirmationContent ===
                                    "function"
                                      ? deleteConfirmationContent(value, index)
                                      : deleteConfirmationContent,
                                  deleteConfirmationKey:
                                    deleteConfirmationKey &&
                                    value?.[deleteConfirmationKey],
                                  deleteConfirmationTitle:
                                    typeof deleteConfirmationTitle ===
                                    "function"
                                      ? deleteConfirmationTitle(value, index)
                                      : deleteConfirmationTitle,
                                  restoreConfirmationContent:
                                    typeof restoreConfirmationContent ===
                                    "function"
                                      ? restoreConfirmationContent(value, index)
                                      : restoreConfirmationContent,
                                  restoreConfirmationKey:
                                    restoreConfirmationKey &&
                                    value?.[restoreConfirmationKey],
                                  restoreConfirmationTitle:
                                    typeof restoreConfirmationTitle ===
                                    "function"
                                      ? restoreConfirmationTitle(value, index)
                                      : restoreConfirmationTitle,
                                  tooltipDelete,
                                  tooltipEdit,
                                  tooltipRestore,
                                  tooltipView,
                                  disabledView:
                                    typeof disabledView === "function"
                                      ? disabledView(value, index)
                                      : disabledView,
                                  disabledEdit:
                                    typeof disabledEdit === "function"
                                      ? disabledEdit(value, index)
                                      : disabledEdit,
                                  disabledDelete:
                                    typeof disabledDelete === "function"
                                      ? disabledDelete(value, index)
                                      : disabledDelete,
                                  disabledRestore:
                                    typeof disabledRestore === "function"
                                      ? disabledRestore(value, index)
                                      : disabledRestore,
                                  checked:
                                    typeof checkedAction === "function"
                                      ? checkedAction(value, index)
                                      : checkedAction,
                                  handleCheckbox:
                                    handleCheckboxAction &&
                                    (e =>
                                      handleCheckboxAction(e, value, index)),
                                  customAction:
                                    typeof customAction === "function"
                                      ? () => customAction(value, index)
                                      : null,
                                  tooltipCheckbox:
                                    typeof tooltipCheckboxAction === "function"
                                      ? tooltipCheckboxAction(value, index)
                                      : tooltipCheckboxAction,
                                  disabledCheckbox:
                                    typeof disabledCheckboxAction === "function"
                                      ? disabledCheckboxAction(value, index)
                                      : disabledCheckboxAction,
                                }}
                              />
                            )}
                          </TableBodyCell>
                        )}
                        {columnTable?.map((item, idx) => (
                          <TableBodyCell
                            key={idx}
                            width={item?.width}
                            maxWidth={
                              !item?.width && `${1 / columnTable?.length}%`
                            }
                            highlight={item?.highlight}
                            backgroundColor={
                              item?.highlight && (item?.color || theme)
                            }
                            dense={item?.dense}
                          >
                            <Typography fontSize="14px" color="#111827">
                              {item?.renderText
                                ? item?.renderText(
                                    item?.name !== "all"
                                      ? value?.[item?.name]
                                      : value || "-",
                                    value,
                                    index
                                  )
                                : value?.[item?.name] || "-"}
                            </Typography>
                          </TableBodyCell>
                        ))}
                        {isExpandable && (
                          <TableBodyCell>
                            <IconButton
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                handleToggleRow(value);
                                handleExpandableRow(value);
                              }}
                            >
                              {openRow ===
                              (value?.[expandedRowKey] || value) ? (
                                <KeyboardArrowUp />
                              ) : (
                                <KeyboardArrowDown />
                              )}
                            </IconButton>
                          </TableBodyCell>
                        )}
                      </TableRow>
                      {isExpandable && (
                        <TableRow>
                          <TableCell
                            colSpan={
                              columnTable.length +
                              (isHaveCheckbox ? 1 : 0) +
                              (isHaveAction ? 1 : 0) +
                              1
                            }
                            sx={{ p: 0, borderBottom: "none" }}
                          >
                            <Collapse
                              in={
                                openRow === (value?.[expandedRowKey] || value)
                              }
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box bgcolor="#ffffff">
                                {renderExpandedRow?.(value)}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={12}>
                      <center>
                        <Typography
                          fontWeight={400}
                          color="#687083"
                          fontSize="14px"
                          fontStyle="italic"
                        >
                          {emptyPlaceHolder || "No Data"}
                        </Typography>
                      </center>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CustomTableContainer>
          {pageCount > 0 && (
            <TablePagination
              color={color}
              page={page}
              pageSize={pageSize}
              pageCount={pageCount}
              handleChangePage={handleChangePage}
              handleChangePageSize={handleChangePageSize}
              pageSizeList={pageSizeList}
              hideLastPage={hideLastPage}
              siblingCount={rest.siblingCount}
              boundaryCount={rest.boundaryCount}
              size={rest.paginationSize || rest.size}
            />
          )}
        </Box>
      )}
    </Box>
  );
};
const CustomTableContainer = styled(TableContainer)(({ maxHeight }) => ({
  maxHeight: maxHeight,
  borderRadius: "6px",
}));
const TableHeadCell = styled(TableCell)(({ width, maxWidth, dense }) => ({
  backgroundColor: "#fafafa",
  width: width || "auto",
  maxWidth: maxWidth,
  padding: dense && "0 16px",
}));
const TableBodyCell = styled(TableCell)(
  ({ width, maxWidth, highlight, backgroundColor, dense }) => ({
    width: width || "auto",
    maxWidth: maxWidth,
    backgroundColor: highlight && `${backgroundColor}10`,
    padding: dense && "0 16px",
  })
);

export default CustomTable;
